/**
 * What a call looks like in each of its five states, with no React in sight.
 *
 * The desktop's `CallWindow` is five states and three overlapping button sets
 * (`relay/src/ui/callwindow.cpp`, `rebuildControls`), and its own comment says
 * why they are rebuilt rather than shown and hidden: the button a stale set
 * leaves behind is usually Answer. The browser has the same hazard and the same
 * answer — one function says which controls exist, the bar renders exactly
 * that, and the mapping is a table a test can read.
 *
 * Nothing here touches media, the store or the DOM, which is what makes it the
 * one part of the call UI a node test can check.
 */

import type { CallStatus, RemotePeer } from '../../lib/calls/types';

/** The five, named as the person in the call would name them. */
export type CallUiState = 'ringing' | 'outgoing' | 'connecting' | 'active' | 'ended';

export type CallControl = 'answer' | 'decline' | 'mute' | 'camera' | 'screen' | 'deafen' | 'hangUp';

/**
 * The state a person is in, from the session's state and who else is here.
 *
 * `CallStatus` is about the SFU and `CallUiState` is about the call. A caller
 * reaches `inCall` the moment its own tracks are published, which is long
 * before anybody picks up — so an empty room is still ringing out, and
 * `connected` (somebody else has a seat) is what tells the two apart.
 */
export function uiStateOf(
  status: CallStatus,
  direction: 'incoming' | 'outgoing',
  connected: boolean
): CallUiState {
  if (status === 'ended') return 'ended';
  if (status === 'idle' || (status === 'inCall' && !connected)) {
    return direction === 'incoming' ? 'ringing' : 'outgoing';
  }
  if (status === 'inCall') return 'active';
  return 'connecting';
}

const RINGING: readonly CallControl[] = ['answer', 'decline'];
const LIVE: readonly CallControl[] = ['mute', 'camera', 'screen', 'deafen', 'hangUp'];
const NONE: readonly CallControl[] = [];

/**
 * Which controls the bar holds.
 *
 * An incoming call has two and only two: a Mute button beside an unanswered
 * ring mutes a microphone nothing is listening to. An ended call has none —
 * there is nothing left to press.
 */
export function controlsFor(state: CallUiState): readonly CallControl[] {
  if (state === 'ringing') return RINGING;
  if (state === 'ended') return NONE;
  return LIVE;
}

/** Hang up, cancel and decline are three words for ending a call and are not
 *  the same event. The button that ends one you started says Cancel. */
export function hangUpKey(state: CallUiState): string {
  return state === 'outgoing' ? 'call.cancel' : 'call.hangup';
}

/** The line under the name. */
export function statusKey(state: CallUiState): string {
  return `call.state.${state}`;
}

/**
 * Why the media never opened, when that is why the call ended.
 *
 * `join()` opens the microphone before anything else can go wrong, so a refused
 * or missing one is by far the most common way a call ends with no call — and
 * "Call ended." for it is a true sentence that answers nothing. The two names
 * are the ones `getUserMedia` actually rejects with; everything else is the
 * ordinary ending and says so.
 */
export function mediaFailureKey(error: unknown): string | null {
  const name = (error as { name?: unknown } | null | undefined)?.name;
  if (name === 'NotAllowedError') return 'call.media.refused';
  if (name === 'NotFoundError') return 'call.media.missing';
  return null;
}

/**
 * Who the speaker view shows.
 *
 * A share wins over a voice: somebody presenting is what everyone came to look
 * at, and it does not stop being that while they pause for breath. Then whoever
 * is speaking, then the first seat, so the view is never empty while anybody is
 * in the room.
 */
export function speakerOf(peers: readonly RemotePeer[]): RemotePeer | null {
  return (
    peers.find((p) => p.tracks.screen) ?? peers.find((p) => p.speaking) ?? peers[0] ?? null
  );
}
